import React from 'react';
import {
    displayShortDateOrNA,
    displayPercentOrNaTruthyZero,
    displayCurrencyOrNATruthyZero,
    NA_DEFAULT
} from '../../utils/Format/Format';

const sortByDateField = (list, dateKey) =>
    list.sort((a, b) => {
        if (!(a || b)) return 0;
        const aDate = new Date(a[dateKey]);
        const bDate = new Date(b[dateKey]);
        if (!(aDate || bDate)) return 0;
        // newest date will be first
        return bDate - aDate;
    });

const ignoreEntriesAfterMonthEnd = (list, dateKey, monthEnd) => {
    if (!(list && list.length && monthEnd)) return null;

    const monthEndDate = new Date(monthEnd);
    if (!monthEndDate) return null;

    // return new first entry after throwing out any entries
    // that have a date greater than the month end date
    const filteredList = list.filter((entry) => {
        const entryDate = new Date(entry[dateKey]);
        return entryDate <= monthEndDate;
    });

    return filteredList[0];
};

export const monthlyDistributionUtil = (gqlResult) => {
    if (!gqlResult) return null;

    const {
        averageAnnualTotalReturns,
        thirtyDaySECDividend,
        thirtyDaySECDividendAsOfDate,
        unsubsidizedThirtyDaySECYield,
        unsubsidizedThirtyDaySECYieldAsOfDate,
        distribution,
        distributionMetrics
    } =
        gqlResult.exchangeTradedFund || {};

    const { quarterly } = averageAnnualTotalReturns || {};
    const { asOfDate: quarterlyAsOfDate } = quarterly || {};
    const { distributionFrequency, distributions } = distribution || {};
    const { asOfDate: distDataAsOfDate, distMetricsData } =
        distributionMetrics || {};

    // convert the asOfDate to a boolean to determine if we can show the data
    const canShow = !!quarterlyAsOfDate;

    let currentDistMetrics = {};
    let currentDistribution = {};

    if (distMetricsData && distMetricsData.length) {
        // destructure the first item in the sorted array
        const [mostRecentDistMetrics] = sortByDateField(
            distMetricsData,
            'rateDate'
        );
        currentDistMetrics = mostRecentDistMetrics;
    }

    if (distributions && distributions.length) {
        const exDateKey = 'exDate';
        const sortedDistributions = sortByDateField(distributions, exDateKey);
        currentDistribution = ignoreEntriesAfterMonthEnd(
            sortedDistributions,
            exDateKey,
            distDataAsOfDate
        );
    }

    const {
        distributionRate,
        distributionAmount,
        twelveMonthTrailingDistributionRate
    } = currentDistMetrics;

    const headers = [
        `Distribution as of ${displayShortDateOrNA(
            canShow && distDataAsOfDate
        )}`
    ];

    const rows = [
        {
            label: `SEC Standardized Yield w/Waiver as of (${displayShortDateOrNA(
                canShow && thirtyDaySECDividendAsOfDate
            )})`,
            values: [
                {
                    val1: displayPercentOrNaTruthyZero(
                        canShow && thirtyDaySECDividend
                    )
                }
            ],
            id: 'sec-with-waiver',
            tipContent: (
                <React.Fragment>
                    The SEC standardized yield is computed under an SEC
                    standardized formula and reflects an estimated yield to
                    maturity (assuming all portfolio securities are held to
                    maturity); it is available the first month after the fund’s
                    inception.<br />
                    <br />SEC Yield w/o Waiver excludes the effect of
                    contractual expense limitation arrangements.
                </React.Fragment>
            )
        },
        {
            label: `SEC Standardized Yield w/o Waiver as of (${displayShortDateOrNA(
                canShow && unsubsidizedThirtyDaySECYieldAsOfDate
            )})`,
            values: [
                {
                    val1: displayPercentOrNaTruthyZero(
                        canShow && unsubsidizedThirtyDaySECYield
                    )
                }
            ],
            id: 'sec-without-waiver',
            tipContent: (
                <React.Fragment>
                    The SEC standardized yield is computed under an SEC
                    standardized formula and reflects an estimated yield to
                    maturity (assuming all portfolio securities are held to
                    maturity); it is available the first month after the fund’s
                    inception.<br />
                    <br />SEC Yield w/o Waiver excludes the effect of
                    contractual expense limitation arrangements.
                </React.Fragment>
            )
        },
        {
            label: 'Distribution Schedule',
            values: [
                {
                    val1: distributionFrequency || NA_DEFAULT
                }
            ],
            id: 'dist-schedule-row'
        },
        {
            label: 'Distribution Rate',
            values: [
                {
                    val1: displayPercentOrNaTruthyZero(
                        canShow && distributionRate
                    )
                }
            ],
            id: 'dist-rate-row',
            tipContent:
                'The Distribution Rate represents an annualized percentage of a single monthly distribution (per share) multiplied by 12 and divided by the closing NAV on the ex-dividend date. Distribution amounts and NAVs will vary. Distribution amounts may be comprised from multiple sources including income, capital gains or return of capital – tax implications will vary. The Distribution Rate is not an indication of total return.'
        },
        {
            label: '12-Month Trailing Distribution Rate',
            values: [
                {
                    val1: displayPercentOrNaTruthyZero(
                        canShow && twelveMonthTrailingDistributionRate
                    )
                }
            ],
            id: 'dist-trailing-row',
            tipContent:
                'The 12-Month Trailing Distribution Rate represents the annualized percentage of realized distributions (per share) by adding all of the prior rolling year’s distributions divided by the closing NAV on the most recent ex-dividend date. Distribution amounts and NAVs will vary. Distribution amounts may be comprised from multiple sources including income, capital gains or return of capital – tax implications will vary. The 12-Month Distribution Rate is not an indication of total return.'
        },
        {
            label: 'Distribution Amount / Share ($)',
            values: [
                {
                    val1: displayCurrencyOrNATruthyZero(
                        canShow &&
                            currentDistribution &&
                            currentDistribution.totalDistribution
                    ) // $XXXX.XX
                }
            ],
            id: 'dist-amount-row'
        },
        {
            label: 'Distribution Amount / Share (%)',
            values: [
                {
                    val1: displayPercentOrNaTruthyZero(
                        canShow && distributionAmount // distributionAmountPerSharePercentage
                    )
                }
            ],
            id: 'dist-rate-share-row'
        }
    ];

    return {
        headers,
        rows
    };
};
